import { Button, FieldBlock, Modal, Select, Textarea, useToast } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api.ts'

/**
 * Reabrir um Item de Trabalho (pedido 2026-08-30).
 *
 * Sair de um status terminal não é uma troca qualquer: a API exige o MOTIVO, e
 * o item precisa voltar para onde o trabalho recomeça — senão fica parado na
 * etapa em que foi concluído. Por isso a reabertura passa por aqui, com as duas
 * coisas juntas e uma confirmação explícita.
 */
export function ReabrirModal({
  aberto,
  codigo,
  statusDestino,
  workItemTypeId,
  etapaAtual,
  onFechar,
  onConfirmar,
  salvando = false,
}: {
  aberto: boolean
  codigo: string
  /** Rótulo do status para onde o item vai. */
  statusDestino: string
  workItemTypeId: string
  etapaAtual: string | null
  onFechar: () => void
  onConfirmar: (dados: { comment: string; flowStepId: string | null }) => void
  salvando?: boolean
}) {
  const toast = useToast()
  const [motivo, setMotivo] = useState('')
  const [etapa, setEtapa] = useState<string | null>(etapaAtual)

  /** Etapas dos fluxos do TIPO do item — as únicas para onde ele pode voltar. */
  const nos = useQuery({
    queryKey: ['flow-nodes'],
    queryFn: async () => (await api.flows.nodes.$get()).json(),
    enabled: aberto,
    retry: false,
  })
  const tipos = useQuery({
    queryKey: ['catalog', 'bootstrap'],
    queryFn: async () => (await api.catalog.bootstrap.$get()).json(),
    enabled: aberto,
  })
  const doTipo = new Set(
    ((tipos.data?.workItemTypes ?? []).find((t) => t.id === workItemTypeId) as { flowIds?: string[] } | undefined)
      ?.flowIds ?? [],
  )
  const opcoes = (nos.data?.nodes ?? [])
    .filter((n) => doTipo.size === 0 || doTipo.has(n.flowId))
    .map((n) => ({ value: n.flowStepId, label: `${n.flowName} · ${n.stepName}` }))

  if (!aberto) return null
  return (
    <Modal
      open
      onClose={onFechar}
      title={`Reabrir ${codigo}`}
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            icon="fas fa-rotate-left"
            disabled={salvando}
            onClick={() => {
              if (!motivo.trim()) {
                toast.push({ tone: 'warning', message: 'Informe o motivo da reabertura.' })
                return
              }
              onConfirmar({ comment: motivo.trim(), flowStepId: etapa })
            }}
          >
            {salvando ? 'Reabrindo…' : 'Confirmar reabertura'}
          </Button>
        </>
      }
    >
      <div className="ob-stack">
        <p className="rea__aviso">
          O item volta para <strong>{statusDestino}</strong>. O motivo fica no histórico.
        </p>
        <FieldBlock label="Motivo da reabertura" required>
          {({ id }) => (
            <Textarea
              id={id}
              rows={3}
              autoFocus
              placeholder="O que faltou, ou o que mudou?"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          )}
        </FieldBlock>
        <FieldBlock label="Volta para a etapa" hint="Onde o trabalho recomeça no fluxo.">
          {({ id }) => (
            <Select
              id={id}
              value={etapa ?? ''}
              placeholder={opcoes.length === 0 ? 'Este tipo não segue fluxo' : 'Escolha a etapa'}
              options={opcoes}
              disabled={opcoes.length === 0}
              onChange={(v) => setEtapa(v || null)}
            />
          )}
        </FieldBlock>
      </div>
    </Modal>
  )
}
